// services/sheetService.js - Version complète avec support availableSections + getRidersProgress

import { config } from '../config';
import cacheService from './cacheService';

class ApiError extends Error {
  constructor(message, status, type) {
    super(message);
    this.status = status;
    this.type = type;
  }
}

const handleResponse = async (response) => {
  if (!response.ok) {
    const errorType = response.status === 401 ? 'session' : 
                     response.status >= 500 ? 'server' : 'network';
    
    throw new ApiError(
      `Request failed: ${response.statusText}`, 
      response.status, 
      errorType
    );
  }
  return response.json();
};

// Fonction générique avec cache
const fetchWithCache = async (url, options = {}, useCache = true) => {
  const cacheKey = cacheService.generateKey(url);
  
  // Vérifier le cache d'abord
  if (useCache) {
    const cachedData = cacheService.get(cacheKey);
    if (cachedData) {
      console.log('Cache hit:', url);
      return cachedData;
    }
  }
  
  // Fetch depuis l'API
  console.log('API call:', url);
  const response = await fetch(url, options);
  const data = await handleResponse(response);
  
  // Mettre en cache seulement si c'est une lecture réussie
  if (useCache && options.method !== 'PUT') {
    cacheService.set(cacheKey, data);
  }
  
  return data;
};

// 🆕 Fonction modifiée pour supporter includeHidden
const getSheetsList = async (sheetId, getValidAccessToken, includeHidden = false) => {
  const sessionId = await getValidAccessToken();
  
  // 🆕 Construire l'URL avec le paramètre includeHidden si nécessaire
  let url = `${config.API_BASE}/sheets/${sheetId}/list`;
  if (includeHidden) {
    url += '?includeHidden=true';
  }
  
  // 🆕 Cache séparé pour les requêtes avec/sans onglets cachés
  const cacheKey = cacheService.generateKey(url);
  
  return fetchWithCache(
    url,
    { headers: { Authorization: `Bearer ${sessionId}` } },
    true // Utiliser le cache
  );
};

const getRiders = async (sheetId, sheetName, getValidAccessToken) => {
  const sessionId = await getValidAccessToken();
  
  return fetchWithCache(
    `${config.API_BASE}/sheets/${sheetId}/riders?sheetName=${encodeURIComponent(sheetName)}`,
    { headers: { Authorization: `Bearer ${sessionId}` } },
    true // Utiliser le cache
  );
};

// 🔧 CORRECTION PRINCIPALE : Support des availableSections
const getRiderData = async (sheetId, sheetName, getValidAccessToken) => {
  const sessionId = await getValidAccessToken();
  
  const structureData = await fetchWithCache(
    `${config.API_BASE}/sheets/${sheetId}/structure?sheetName=${encodeURIComponent(sheetName)}`,
    { headers: { Authorization: `Bearer ${sessionId}` } },
    true // Utiliser le cache
  );
  
  // 🆕 Retourner avec availableSections inclus
  return {
    laps: structureData.laps || 0,
    sections: structureData.sections || 0,
    availableSections: structureData.availableSections || 
                      Array.from({length: structureData.sections || 0}, (_, i) => i + 1) // Fallback
  };
};

// 🆕 NOUVELLE FONCTION pour obtenir uniquement la structure brute
const getRiderStructure = async (sheetId, sheetName, getValidAccessToken) => {
  const sessionId = await getValidAccessToken();
  
  return fetchWithCache(
    `${config.API_BASE}/sheets/${sheetId}/structure?sheetName=${encodeURIComponent(sheetName)}`,
    { headers: { Authorization: `Bearer ${sessionId}` } },
    true // Utiliser le cache
  );
};

const getRiderScore = async (sheetId, sheetName, riderName, getValidAccessToken) => {
  const sessionId = await getValidAccessToken();
  
  return fetchWithCache(
    `${config.API_BASE}/sheets/${sheetId}/scores?sheetName=${encodeURIComponent(sheetName)}&riderName=${encodeURIComponent(riderName)}`,
    { headers: { Authorization: `Bearer ${sessionId}` } },
    false // PAS de cache pour les scores (données en temps réel)
  );
};

// 🆕 NOUVELLE FONCTION pour la progression des pilotes
const getRidersProgress = async (sheetId, sheetName, getValidAccessToken) => {
  const sessionId = await getValidAccessToken();
  
  return fetchWithCache(
    `${config.API_BASE}/sheets/${sheetId}/progress?sheetName=${encodeURIComponent(sheetName)}`,
    { headers: { Authorization: `Bearer ${sessionId}` } },
    false // PAS de cache pour la progression (données en temps réel)
  );
};

const updateScore = async (
  sheetId, 
  sheetName, 
  riderName, 
  lapSection, 
  score, 
  getValidAccessToken
) => {
  const sessionId = await getValidAccessToken();
  
  const response = await fetch(`${config.API_BASE}/sheets/${sheetId}/score`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${sessionId}`
    },
    body: JSON.stringify({
      sheetName,
      riderName,
      lapSection,
      score
    })
  });
  
  return handleResponse(response);
};

// 🆕 FONCTION pour vider le cache du serveur (admin uniquement)
const clearServerCache = async (getValidAccessToken) => {
  const sessionId = await getValidAccessToken();
  
  const response = await fetch(`${config.API_BASE}/cache/clear`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${sessionId}`
    }
  });
  
  return handleResponse(response);
};

// 🆕 FONCTION pour obtenir les informations de session
const getSessionInfo = async (getValidAccessToken) => {
  const sessionId = await getValidAccessToken();
  
  return fetchWithCache(
    `${config.API_BASE}/session/info`,
    { headers: { Authorization: `Bearer ${sessionId}` } },
    false // Pas de cache pour les infos de session
  );
};

// Fonction pour vider le cache client manuellement
const clearCache = () => {
  cacheService.clear();
  console.log('Client cache cleared');
};

// 🆕 FONCTION helper pour valider une structure de course
const validateCourseStructure = (courseData) => {
  if (!courseData || typeof courseData !== 'object') {
    return false;
  }
  
  const { laps, sections, availableSections } = courseData;
  
  // Vérifications de base
  if (typeof laps !== 'number' || laps < 0) return false;
  if (typeof sections !== 'number' || sections < 0) return false;
  
  // Vérifier availableSections si présent
  if (availableSections) {
    if (!Array.isArray(availableSections)) return false;
    
    // Tous les éléments doivent être des nombres positifs
    if (!availableSections.every(s => typeof s === 'number' && s > 0)) {
      return false;
    }
    
    // Les secteurs doivent être triés et uniques
    const sorted = [...availableSections].sort((a, b) => a - b);
    if (JSON.stringify(sorted) !== JSON.stringify(availableSections)) {
      console.warn('Available sections should be sorted:', availableSections);
    }
  }
  
  return true;
};

// 🆕 FONCTION helper pour générer les secteurs par défaut
const generateDefaultSections = (maxSections) => {
  if (typeof maxSections !== 'number' || maxSections <= 0) {
    return [];
  }
  return Array.from({length: maxSections}, (_, i) => i + 1);
};

// Nettoyage automatique du cache client toutes les 10 minutes
setInterval(() => {
  cacheService.cleanup();
}, 10 * 60 * 1000);

// Export par défaut avec toutes les fonctions
export default {
  // Fonctions principales
  getSheetsList,
  getRiders,
  getRiderData,
  getRiderStructure, // 🆕 Nouvelle fonction
  getRiderScore,
  getRidersProgress, // 🆕 NOUVELLE FONCTION pour la progression
  updateScore,
  
  // Fonctions de gestion
  clearCache,
  clearServerCache, // 🆕 Nouvelle fonction
  getSessionInfo, // 🆕 Nouvelle fonction
  
  // Fonctions utilitaires
  validateCourseStructure, // 🆕 Nouvelle fonction
  generateDefaultSections, // 🆕 Nouvelle fonction
  
  // Classes d'erreur
  ApiError
};